import { useEffect, useMemo, useState } from 'react';
import {
  Wrench, Plus, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2,
  Clock, ClipboardList, Trash2, Bell,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';

interface MaintenanceSchedulerPanelProps {
  clientId: string;
  token: string;
}

interface Asset { id: string; type: string; name: string; status: string; online: boolean; lastDataAt: string | null; }
interface WorkOrder {
  id: string; assetType: string; assetId: string; assetName: string; title: string; description: string | null;
  type: string; priority: string; status: string; assignedTo: string | null; assignedToName: string | null;
  scheduledDate: string | null; dueDate: string | null; progressPct: number; startedAt: string | null; completedAt: string | null;
  rootCauseCategory: string | null; rootCauseNotes: string | null;
}
interface MaintenanceWindow { id: string; title: string; description: string | null; startTime: string; endTime: string; status: string; }
interface TeamUser { id: string; name: string; email: string; }

// Status palette (fixed roles, never reused for series identity)
const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b', muted: '#898781' };
// Categorical palette slots 1/2/3 for work-order type identity
const TYPE_COLOR: Record<string, string> = { preventive: '#2a78d6', corrective: '#eb6834', predictive: '#1baf7a' };
const HEALTH_COLOR: Record<string, string> = {
  operational: STATUS.good, under_maintenance: '#2a78d6', awaiting_parts: STATUS.warning, out_of_service: STATUS.critical,
};
const PRIORITY_COLOR: Record<string, string> = { low: STATUS.good, medium: STATUS.warning, high: STATUS.serious, critical: STATUS.critical };

const ROOT_CAUSE_CATEGORIES = [
  'Equipment Failure', 'Human Error', 'Process Issue', 'Supply Chain', 'Cyber / IT', 'Other',
];

const emptyWoForm = {
  id: '', assetType: '', assetId: '', assetName: '', title: '', description: '',
  type: 'preventive', priority: 'medium', assignedTo: '', assignedToName: '', scheduledDate: '', dueDate: '',
};
const emptyWindowForm = { title: '', description: '', startTime: '', endTime: '' };

export function MaintenanceSchedulerPanel({ clientId, token }: MaintenanceSchedulerPanelProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthCursor, setMonthCursor] = useState(new Date());

  const [woFormOpen, setWoFormOpen] = useState(false);
  const [woForm, setWoForm] = useState(emptyWoForm);
  const [windowFormOpen, setWindowFormOpen] = useState(false);
  const [windowForm, setWindowForm] = useState(emptyWindowForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [rcaTargetId, setRcaTargetId] = useState<string | null>(null);
  const [rcaCategory, setRcaCategory] = useState('');
  const [rcaNotes, setRcaNotes] = useState('');
  const [rcaError, setRcaError] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadAll = async () => {
    try {
      const [assetsRes, woRes, winRes, statsRes, usersRes] = await Promise.all([
        fetch(`${API_BASE}/api/clients/${clientId}/assets`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/clients/${clientId}/work-orders`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/clients/${clientId}/maintenance-windows`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/clients/${clientId}/maintenance-stats`, { headers: authHeaders }),
        fetch(`${API_BASE}/api/clients/${clientId}/user-activity`, { headers: authHeaders }),
      ]);
      const [assetsP, woP, winP, statsP, usersP] = await Promise.all([assetsRes.json(), woRes.json(), winRes.json(), statsRes.json(), usersRes.json()]);
      if (assetsP?.success) setAssets(assetsP.assets || []);
      if (woP?.success) setWorkOrders(woP.workOrders || []);
      if (winP?.success) setWindows(winP.windows || []);
      if (statsP?.success) setStats(statsP);
      if (usersP?.success) setTeamUsers((usersP.users || []).map((u: any) => ({ id: u.id, name: u.name, email: u.email })));
    } catch (_) { /* keep last known state */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!clientId || !token) return;
    loadAll();
    const ws = getWebSocketClient(token, { clientId });
    const unsub1 = ws.on('work_order_update', () => loadAll());
    const unsub2 = ws.on('maintenance_update', () => loadAll());
    return () => { unsub1(); unsub2(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const healthCounts = useMemo(() => {
    const counts: Record<string, number> = { operational: 0, under_maintenance: 0, awaiting_parts: 0, out_of_service: 0 };
    for (const a of assets) counts[a.status] = (counts[a.status] || 0) + 1;
    return counts;
  }, [assets]);

  const donutData = useMemo(() => (
    Object.entries(healthCounts).filter(([, v]) => v > 0).map(([k, v]) => ({
      name: k.replace('_', ' '), value: v, color: HEALTH_COLOR[k],
    }))
  ), [healthCounts]);

  const filteredWorkOrders = useMemo(() => (
    statusFilter === 'all' ? workOrders : workOrders.filter((w) => w.status === statusFilter)
  ), [workOrders, statusFilter]);

  const upcomingWorkOrders = useMemo(() => (
    workOrders
      .filter((w) => w.status !== 'completed' && w.status !== 'cancelled' && w.dueDate && new Date(w.dueDate).getTime() > Date.now())
      .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
      .slice(0, 5)
  ), [workOrders]);

  const overdueWorkOrders = useMemo(() => (
    workOrders.filter((w) => w.status !== 'completed' && w.status !== 'cancelled' && w.dueDate && new Date(w.dueDate).getTime() < Date.now())
  ), [workOrders]);

  // Simple month grid — Sun..Sat, current month, events overlaid by date
  const calendarCells = useMemo(() => {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: Date | null }[] = [];
    for (let i = 0; i < startOffset; i++) cells.push({ date: null });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d) });
    return cells;
  }, [monthCursor]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, { label: string; color: string }[]>();
    const key = (d: Date) => d.toDateString();
    for (const w of workOrders) {
      if (!w.dueDate) continue;
      const d = new Date(w.dueDate);
      const list = map.get(key(d)) || [];
      list.push({ label: `${w.type === 'corrective' ? 'CM' : w.type === 'predictive' ? 'PD' : 'PM'} - ${w.assetName || w.title}`, color: TYPE_COLOR[w.type] || TYPE_COLOR.preventive });
      map.set(key(d), list);
    }
    for (const win of windows) {
      const d = new Date(win.startTime);
      const list = map.get(key(d)) || [];
      list.push({ label: win.title, color: '#4a3aa7' });
      map.set(key(d), list);
    }
    return map;
  }, [workOrders, windows]);

  const openCreateWorkOrder = () => {
    setWoForm(emptyWoForm);
    setError('');
    setWoFormOpen(true);
  };

  const saveWorkOrder = async () => {
    if (!woForm.assetId || !woForm.title.trim()) {
      setError('Asset and title are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/work-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          assetType: woForm.assetType, assetId: woForm.assetId, assetName: woForm.assetName,
          title: woForm.title.trim(), description: woForm.description.trim() || null,
          type: woForm.type, priority: woForm.priority,
          assignedTo: woForm.assignedTo || null, assignedToName: woForm.assignedToName || null,
          scheduledDate: woForm.scheduledDate ? new Date(woForm.scheduledDate).toISOString() : null,
          dueDate: woForm.dueDate ? new Date(woForm.dueDate).toISOString() : null,
        }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.error || 'Failed to create work order');
      setWoFormOpen(false);
      loadAll();
    } catch (err: any) {
      setError(err?.message || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };

  const updateWorkOrderStatus = async (id: string, status: string, rootCause?: { category: string; notes: string }) => {
    try {
      await fetch(`${API_BASE}/api/clients/${clientId}/work-orders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          status,
          ...(rootCause ? { rootCauseCategory: rootCause.category, rootCauseNotes: rootCause.notes } : {}),
        }),
      });
      loadAll();
    } catch (_) { /* non-fatal */ }
  };

  // Completing a work order for the first time requires a root cause — this
  // is the signal a future root-cause view (and any copilot on top of it)
  // depends on, so it's captured here instead of skipped as optional.
  const handleStatusChange = (wo: WorkOrder, status: string) => {
    if (status === 'completed' && !wo.completedAt) {
      setRcaTargetId(wo.id);
      setRcaCategory('');
      setRcaNotes('');
      setRcaError('');
      return;
    }
    updateWorkOrderStatus(wo.id, status);
  };

  const confirmRootCause = async () => {
    if (!rcaTargetId) return;
    if (!rcaCategory) {
      setRcaError('Select a root cause category to complete this work order');
      return;
    }
    await updateWorkOrderStatus(rcaTargetId, 'completed', { category: rcaCategory, notes: rcaNotes.trim() });
    setRcaTargetId(null);
  };

  const deleteWorkOrder = async (id: string) => {
    try {
      await fetch(`${API_BASE}/api/clients/${clientId}/work-orders/${id}`, { method: 'DELETE', headers: authHeaders });
      setWorkOrders((prev) => prev.filter((w) => w.id !== id));
    } catch (_) { /* non-fatal */ }
  };

  const saveWindow = async () => {
    if (!windowForm.title.trim() || !windowForm.startTime || !windowForm.endTime) {
      setError('Title, start, and end are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/maintenance-windows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          title: windowForm.title.trim(),
          description: windowForm.description.trim() || null,
          startTime: new Date(windowForm.startTime).toISOString(),
          endTime: new Date(windowForm.endTime).toISOString(),
        }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.error || 'Failed to schedule downtime window');
      setWindowFormOpen(false);
      setWindowForm(emptyWindowForm);
      loadAll();
    } catch (err: any) {
      setError(err?.message || 'Failed to schedule downtime window');
    } finally {
      setSaving(false);
    }
  };

  const statTiles = stats ? [
    { label: 'Total Assets', value: stats.totalAssets, icon: Wrench, sub: 'All PLCs, meters & scanners' },
    { label: 'Upcoming (7 Days)', value: stats.upcomingCount, icon: Clock, sub: 'Due soon', color: '#2a78d6' },
    { label: 'Overdue', value: stats.overdueCount, icon: AlertTriangle, sub: 'Needs attention', color: stats.overdueCount > 0 ? STATUS.critical : STATUS.muted },
    { label: 'In Progress', value: stats.inProgressCount, icon: ClipboardList, sub: 'Active work orders' },
    { label: 'Completed (Month)', value: stats.completedThisMonth, icon: CheckCircle2, sub: 'This calendar month', color: STATUS.good },
    { label: 'MTTR', value: stats.mttrHours != null ? `${stats.mttrHours.toFixed(1)}h` : '—', icon: Clock, sub: 'Mean time to repair (corrective)' },
  ] : [];

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading maintenance data...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Maintenance Scheduler</h2>
          <p className="text-sm text-muted-foreground">Plan, schedule, and track maintenance across your real assets.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setWindowForm(emptyWindowForm); setError(''); setWindowFormOpen(true); }}>
            <Bell className="h-4 w-4 mr-1" /> Schedule Downtime
          </Button>
          <Button onClick={openCreateWorkOrder}>
            <Plus className="h-4 w-4 mr-1" /> New Work Order
          </Button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {statTiles.map((t) => (
          <div key={t.label} className="border rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] font-medium mb-1 uppercase tracking-wide">
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </div>
            <p className="text-xl font-semibold" style={t.color ? { color: t.color } : undefined}>{t.value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{t.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Calendar */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Maintenance Calendar</h3>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium w-32 text-center">{monthCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
                <Button variant="ghost" size="icon" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarCells.map((cell, i) => {
                const events = cell.date ? (eventsByDate.get(cell.date.toDateString()) || []) : [];
                const isToday = cell.date && cell.date.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`min-h-[72px] border rounded-md p-1 text-left ${isToday ? 'border-primary' : 'border-border'} ${!cell.date ? 'bg-muted/20' : ''}`}>
                    {cell.date && <span className="text-[11px] text-muted-foreground">{cell.date.getDate()}</span>}
                    <div className="space-y-0.5 mt-0.5">
                      {events.slice(0, 2).map((e, idx) => (
                        <div key={idx} className="text-[10px] truncate rounded px-1 py-0.5 text-white" style={{ backgroundColor: e.color }} title={e.label}>
                          {e.label}
                        </div>
                      ))}
                      {events.length > 2 && <div className="text-[10px] text-muted-foreground">+{events.length - 2} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: TYPE_COLOR.preventive }} /> Preventive</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: TYPE_COLOR.corrective }} /> Corrective</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: TYPE_COLOR.predictive }} /> Predictive</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#4a3aa7' }} /> Downtime window</span>
            </div>
          </div>

          {/* Work orders table */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Work Orders</h3>
              <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="open">Open</TabsTrigger>
                  <TabsTrigger value="in_progress">In Progress</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {filteredWorkOrders.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">No work orders{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
              )}
              {filteredWorkOrders.map((w) => (
                <div key={w.id} className="border rounded-md p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: PRIORITY_COLOR[w.priority] }}>{w.priority}</span>
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: TYPE_COLOR[w.type] }}>{w.type}</span>
                        <p className="font-medium truncate">{w.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{w.assetName} {w.assignedToName ? `• Assigned to ${w.assignedToName}` : ''}</p>
                      {w.dueDate && <p className="text-xs text-muted-foreground">Due {new Date(w.dueDate).toLocaleDateString()}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Select value={w.status} onValueChange={(v) => handleStatusChange(w, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="on_hold">On Hold</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" onClick={() => deleteWorkOrder(w.id)} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <Progress value={w.progressPct} className="h-1.5 mt-2" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Asset health donut */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">Asset Health</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} stroke="none" />)}
                  </Pie>
                  <RTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1 mt-2">
              {donutData.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 capitalize text-muted-foreground"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} /> {d.name}</span>
                  <span className="font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">Upcoming Maintenance</h3>
            <div className="space-y-2">
              {upcomingWorkOrders.length === 0 && <p className="text-xs text-muted-foreground">Nothing due in the next 7 days.</p>}
              {upcomingWorkOrders.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-xs">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{w.assetName}</p>
                    <p className="text-muted-foreground truncate">{w.title}</p>
                  </div>
                  <span className="text-muted-foreground shrink-0 ml-2">{new Date(w.dueDate!).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Alerts */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">Maintenance Alerts</h3>
            <div className="space-y-2">
              {overdueWorkOrders.length === 0 && <p className="text-xs text-muted-foreground">No overdue work orders.</p>}
              {overdueWorkOrders.map((w) => (
                <div key={w.id} className="flex items-start gap-2 text-xs">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: STATUS.critical }} />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{w.assetName} — overdue</p>
                    <p className="text-muted-foreground truncate">{w.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* New work order dialog */}
      <Dialog open={woFormOpen} onOpenChange={setWoFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Asset</Label>
              <Select
                value={woForm.assetId}
                onValueChange={(v) => {
                  const asset = assets.find((a) => a.id === v);
                  setWoForm({ ...woForm, assetId: v, assetType: asset?.type || '', assetName: asset?.name || '' });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select an asset" /></SelectTrigger>
                <SelectContent>
                  {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type.replace('_', ' ')})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={woForm.title} onChange={(e) => setWoForm({ ...woForm, title: e.target.value })} placeholder="e.g. Quarterly filter replacement" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={woForm.description} onChange={(e) => setWoForm({ ...woForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={woForm.type} onValueChange={(v) => setWoForm({ ...woForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventive">Preventive</SelectItem>
                    <SelectItem value="corrective">Corrective</SelectItem>
                    <SelectItem value="predictive">Predictive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={woForm.priority} onValueChange={(v) => setWoForm({ ...woForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assign to (optional)</Label>
              <Select
                value={woForm.assignedTo}
                onValueChange={(v) => {
                  const u = teamUsers.find((tu) => tu.id === v);
                  setWoForm({ ...woForm, assignedTo: v, assignedToName: u?.name || '' });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {teamUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Scheduled Date</Label>
                <Input type="datetime-local" value={woForm.scheduledDate} onChange={(e) => setWoForm({ ...woForm, scheduledDate: e.target.value })} />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="datetime-local" value={woForm.dueDate} onChange={(e) => setWoForm({ ...woForm, dueDate: e.target.value })} />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWoFormOpen(false)}>Cancel</Button>
            <Button onClick={saveWorkOrder} disabled={saving}>{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Root cause capture — required the first time a work order is marked complete */}
      <Dialog open={!!rcaTargetId} onOpenChange={(open) => { if (!open) setRcaTargetId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>What caused this?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Root Cause Category</Label>
              <Select value={rcaCategory} onValueChange={setRcaCategory}>
                <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                <SelectContent>
                  {ROOT_CAUSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={rcaNotes} onChange={(e) => setRcaNotes(e.target.value)} placeholder="What actually happened, and what fixed it" />
            </div>
            {rcaError && <p className="text-sm text-destructive">{rcaError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRcaTargetId(null)}>Cancel</Button>
            <Button onClick={confirmRootCause}>Complete Work Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule downtime window dialog (reuses existing maintenance-windows feature) */}
      <Dialog open={windowFormOpen} onOpenChange={setWindowFormOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Maintenance Downtime</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={windowForm.title} onChange={(e) => setWindowForm({ ...windowForm, title: e.target.value })} placeholder="e.g. Scheduled server maintenance" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={windowForm.description} onChange={(e) => setWindowForm({ ...windowForm, description: e.target.value })} placeholder="What users should expect" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input type="datetime-local" value={windowForm.startTime} onChange={(e) => setWindowForm({ ...windowForm, startTime: e.target.value })} />
              </div>
              <div>
                <Label>End</Label>
                <Input type="datetime-local" value={windowForm.endTime} onChange={(e) => setWindowForm({ ...windowForm, endTime: e.target.value })} />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWindowFormOpen(false)}>Cancel</Button>
            <Button onClick={saveWindow} disabled={saving}>{saving ? 'Saving...' : 'Notify Everyone'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
